import React, { useEffect, useRef, useState } from 'react';
import { Layout, Menu, Button, Badge, Space, Dropdown, Avatar, Breadcrumb, Drawer } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  LoginOutlined,
  LogoutOutlined,
  SettingOutlined,
  DashboardOutlined,
  FileTextOutlined,
  MenuOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { useCartStore } from '../../stores/cartStore';
import { authService } from '../../services/authService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import CartDrawer from '../cart/CartDrawer';
import CartFab from '../cart/CartFab';

const { Header: AntHeader } = Layout;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isGuest, user, logout } = useAuthStore();
  const { getItemCount, fetchCart } = useCartStore();
  const { breadcrumbs } = useBreadcrumb();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Publish the header's real height so fixed overlays (the antd message
  // popup) can sit below it instead of on top of the cart/nav. The height is
  // not a constant: the breadcrumb row adds a second row on pages like the
  // product detail page, and the logo shrinks on small screens.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const publishHeight = () => {
      document.documentElement.style.setProperty(
        '--dasoon-header-height',
        `${Math.round(el.getBoundingClientRect().height)}px`
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      // Admin pages render their own header, so drop back to the CSS default.
      document.documentElement.style.removeProperty('--dasoon-header-height');
    };
  }, []);
  
  const cartItemCount = getItemCount();

  // The floating cart belongs on the product browsing pages the ticket names
  // (issue #125): the list, now on Home (issue #189), and a product's detail page. Everywhere else either
  // already shows the cart (/cart, /checkout) or has nothing to add to it.
  const isProductBrowsing =
    location.pathname === '/' || location.pathname.startsWith('/products/');

  // Load cart only when authenticated or guest, with cleanup
  useEffect(() => {
    let isMounted = true;

    const loadCartData = async () => {
      // Only fetch cart if user is authenticated (including guest users)
      if (isAuthenticated && isMounted) {
        try {
          await fetchCart();
        } catch (error) {
          console.error('Failed to load cart in header:', error);
        }
      }
    };

    loadCartData();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, fetchCart]); // Depend on authentication state and fetchCart

  const handleLogin = () => {
    navigate('/login', { state: { from: location } });
  };

  const handleGuestSignIn = () => {
    navigate('/login', { state: { from: location } });
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      logout();
      navigate('/login');
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // The signed-in account's options. Desktop shows them in a dropdown off the
  // avatar; the mobile drawer lists them in place (issue #156) — a dropdown
  // nested in the drawer flashed shut on tap, because the drawer item's own
  // click closed the drawer and touch fired hover-open then click-close.
  const accountMenuItems = [
    {
      key: 'profile-page',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/profile'),
    },
    {
      key: 'wallet',
      label: 'My Wallet',
      icon: <WalletOutlined />,
      onClick: () => navigate('/wallet'),
    },
    // Subscription menu entry hidden — see #24 (wallet replaces subscriptions)
    ...(user?.type === 'ADMIN' ? [{
      key: 'admin-panel',
      label: 'Admin Panel',
      icon: <DashboardOutlined />,
      onClick: () => navigate('/admin'),
    }] : []),
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingOutlined />,
      onClick: () => navigate('/settings'),
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  const accountLabel = (
    <Space style={{ cursor: 'pointer' }}>
      <Avatar 
        size="small" 
        icon={<UserOutlined />}
        style={{ 
          backgroundColor: 'var(--dasoon-primary)',
          color: 'white'
        }}
      />
      <span>{user?.name || user?.id}</span>
    </Space>
  );

  const menuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: <Link to="/">Home</Link>,
    },
    ...(isAuthenticated && !isGuest ? [{
      key: 'orders',
      icon: <FileTextOutlined />,
      label: <Link to="/profile?tab=orders">My Orders</Link>,
    }] : []),
    {
      key: 'cart',
      icon: <ShoppingCartOutlined />,
      // Opens the side panel rather than routing to /cart, so the product list
      // behind it keeps its page, search and category (issue #121). /cart is
      // still reachable from the panel's "View full cart".
      onClick: () => setCartOpen(true),
      label: (
        <Badge count={cartItemCount} showZero={false}>
          <span>Cart</span>
        </Badge>
      ),
    },
    // Profile/Login Menu Item
    ...(isAuthenticated ? [
      isGuest ? {
        key: 'guest',
        icon: <UserOutlined />,
        label: (
          <Space>
            <span>Guest</span>
            <Button type="link" size="small" onClick={handleGuestSignIn} style={{ color: 'var(--dasoon-primary)', padding: 0 }}>
              Sign In
            </Button>
          </Space>
        ),
      } : {
        key: 'profile',
        icon: <UserOutlined />,
        label: (
          <Dropdown
            menu={{ items: accountMenuItems }}
            placement="bottomRight"
            trigger={['hover', 'click']}
          >
            {accountLabel}
          </Dropdown>
        ),
      }
    ] : [{
      key: 'login',
      icon: <LoginOutlined />,
      label: <Button type="link" onClick={handleLogin} style={{ color: 'var(--dasoon-white)', padding: 0 }}>Login</Button>,
    }]),
  ];

  return (
    <AntHeader ref={headerRef} className="dasoon-header">
      <div className="container">
        {/* Main Navigation Row.
            Explicit flex here (not just the `flex`/`items-center` utility classes,
            which aren't defined in this project) so the logo and the mobile
            hamburger sit on ONE vertically-centred 64px row. Without it the
            hamburger stacked below the logo and overlapped the header's yellow
            bottom border on mobile. */}
        <div
          className="flex items-center h-16"
          style={{ width: '100%', display: 'flex', alignItems: 'center', minHeight: '64px' }}
        >
          <div
            className="flex items-center flex-1"
            style={{ display: 'flex', alignItems: 'center', flex: 1 }}
          >
            <div className="brand-logo" style={{ 
              marginRight: 'var(--spacing-2xl)',
            }}>
              <Link to="/" style={{ 
                color: 'inherit', 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)'
              }}>
                <img 
                  src="/images/dasoon-logo.png" 
                  alt="Dasoon Egg" 
                  className="dasoon-header-logo"
                  style={{ height: '50px', width: 'auto' }}
                />
              </Link>
            </div>
            {/* Desktop Menu */}
            <Menu
              mode="horizontal"
              items={menuItems}
              className="border-none bg-transparent dasoon-main-menu"
              style={{ 
                minWidth: '200px',
                fontSize: 'var(--text-base)',
                fontWeight: 500,
                flex: 1
              }}
            />
            {/* Mobile Menu Button */}
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={handleMobileMenuToggle}
              className="dasoon-mobile-menu-btn"
              style={{ 
                color: 'var(--dasoon-white)',
                fontSize: '18px',
                display: 'none'
              }}
            />
          </div>
        </div>
        
        {/* Breadcrumb Row - only show when breadcrumbs exist */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="dasoon-breadcrumb-row" style={{
            padding: 'var(--spacing-sm) 0',
            borderTop: '1px solid rgba(255, 174, 0, 0.2)'
          }}>
            <Breadcrumb
              className="dasoon-breadcrumb"
              items={breadcrumbs.map(item => ({
                key: item.key,
                title: item.path ? (
                  <Link to={item.path} style={{ color: 'var(--dasoon-white)' }}>
                    {item.icon && <span style={{ marginRight: '4px' }}>{item.icon}</span>}
                    {item.title}
                  </Link>
                ) : (
                  <span style={{ color: 'var(--dasoon-primary)' }}>
                    {item.icon && <span style={{ marginRight: '4px' }}>{item.icon}</span>}
                    {item.title}
                  </span>
                )
              }))}
            />
          </div>
        )}
      </div>
      
      {/* Mobile Drawer Menu */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img 
              src="/images/dasoon-logo.png" 
              alt="Dasoon Egg" 
              style={{ height: '30px', width: 'auto' }}
            />
          </div>
        }
        placement="right"
        onClose={closeMobileMenu}
        open={mobileMenuOpen}
        width={280}
        className="dasoon-mobile-drawer"
      >
        <Menu
          mode="vertical"
          items={menuItems.map(item => (
            item.key === 'profile'
              ? {
                  // A group, not the desktop dropdown: the options sit in the
                  // drawer, ready to tap (issue #156).
                  key: 'profile',
                  type: 'group' as const,
                  label: accountLabel,
                  children: accountMenuItems.map(child => (
                    'onClick' in child
                      ? {
                          ...child,
                          onClick: () => {
                            closeMobileMenu();
                            child.onClick?.();
                          },
                        }
                      : child
                  )),
                }
              : {
                  ...item,
                  // Close the menu AND keep the item's own handler — a bare
                  // `onClick: closeMobileMenu` would overwrite Cart's panel opener.
                  onClick: () => {
                    item.onClick?.();
                    closeMobileMenu();
                  },
                }
          ))}
          className="dasoon-mobile-menu"
        />
      </Drawer>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {isProductBrowsing && (
        <CartFab count={cartItemCount} onClick={() => setCartOpen(true)} />
      )}
    </AntHeader>
  );
};

export default Header;
